import { useQuery } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { FieldsProvider } from "@/providers/FieldsProvider";
import { ViewsProvider } from "@/providers/ViewsProvider";
import { FiltersProvider } from "@/providers/FiltersProvider";
import { MetaTableProvider } from "@/providers/MetaTableProvider";
import { GuestsProvider } from "@/providers/GuestsProvider";
import { GuestLogsProvider } from "@/providers/GuestLogs";
import { GuestCompanionsProvider } from "@/providers/GuestCompanions";
import { GuestAppointmentsProvider } from "@/providers/GuestAppointments";
import { AppointmentGuestProvider } from "@/providers/AppointmentGuestProvider";
import { RoleProvider } from "@/providers/RoleProvider";
import { initializeViewFilterGuests } from "@/lib/api/handlers/ViewFilterHandling";
import { getGuestsByIds } from "@/lib/api/handlers/GuestHandling";
import { InlineLoadingSpinner } from "@/components/LoadingSpinner/InlineLoadingSpinner";
import { PGuestDetails, GUEST_REFETCH_FIELDS } from "./PGuestDetails";
import type { Guest } from "@/types/Guest";
import type { Field } from "@/types/Field";
import type { TableView } from "@/types/TableView";
import type { TableFilter } from "@/types/TableFilter";
import { useEffect } from "react";
import { hasDismissOwner } from "@/lib/a11y/dismissOwner";
import { TooltipLayer } from "@/components/PInfo/PInfo";
import { hintZAbove } from "@/components/PInfo/tooltipLayer";

function isServerError(x: unknown): x is { isServerError: true } {
  return (
    !!x &&
    typeof x === "object" &&
    "isServerError" in x &&
    (x as { isServerError?: boolean }).isServerError === true
  );
}

type Props = {
  eventId: string;
  guestId: string;
  onClose: () => void;
  /** Overlay z-class — raise when opened above a high-z modal (e.g. the
   *  pre-send confirm popups at z-200/z-2000). Default keeps page-level use. */
  zClass?: string;
};

/**
 * Self-contained guest-detail popup usable on any event page that has an
 * `eventId` + a `guestId` (seating, slotting, …). `PGuestDetails` and its
 * subtree consume the full guestlist provider stack
 * (Fields/Views/Filters/MetaTable/Guests/Logs/Companions/Appointments/Role);
 * those providers only exist on the guestlist page, so we fetch the single
 * guest + the view/filter/fields seed here and mount the same stack around it.
 * `EventProvider` + `CapabilitiesProvider` are already supplied by
 * `EventLayoutShell`, so they are not re-mounted.
 */
export function StandaloneGuestDetail({
  eventId,
  guestId,
  onClose,
  zClass = "z-[90]",
}: Props) {
  // View/filter/fields seed — one call also returns `allFields` (FieldsProvider
  // does not self-fetch). `defaultView`/`defaultFilter` are returned when no
  // saved view/filter id is passed.
  const seedQ = useQuery({
    queryKey: ["guestDetailSeed", eventId],
    queryFn: () => initializeViewFilterGuests(null, null, eventId),
    enabled: !!eventId,
  });

  const guestQ = useQuery({
    queryKey: ["guestDetailGuest", eventId, guestId],
    queryFn: () =>
      getGuestsByIds(eventId, [guestId], "", [], GUEST_REFETCH_FIELDS, 0, 1),
    enabled: !!eventId && !!guestId,
  });

  const guest =
    !guestQ.data || isServerError(guestQ.data) ? undefined : guestQ.data[0];

  const seedFailed = !!seedQ.data && isServerError(seedQ.data);
  const guestMissing = guestQ.isSuccess && !guest;

  // Nothing to show → bail out cleanly (no provider stack to mount around).
  useEffect(() => {
    if (seedFailed || guestMissing || guestQ.isError) onClose();
  }, [seedFailed, guestMissing, guestQ.isError, onClose]);

  const loading = seedQ.isLoading || guestQ.isLoading;

  const overlay = (children: React.ReactNode) =>
    createPortal(
      <div
        className={`fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${zClass}`}
        onMouseDown={(e) => {
          // One press, one dismissal — see `hasDismissOwner`.
          if (e.target === e.currentTarget && !hasDismissOwner(e.currentTarget as Element)) onClose();
        }}
      >
        {children}
      </div>,
      document.body,
    );

  if (loading) return overlay(<InlineLoadingSpinner />);

  if (!guest || !seedQ.data || isServerError(seedQ.data)) return null;

  const [view, filter, allFields] = seedQ.data as [
    TableView,
    TableFilter,
    Field[],
  ];

  return (
    // Raised above the hint rung (the send dialog's opt-in list opens it at
    // z-10070), the popup paints its own hints above itself — the pending
    // chip of "Ankunft melden an" among them (audit hop3 r2 L3).
    <TooltipLayer z={hintZAbove(zClass)}>
    <FieldsProvider eventId={eventId} allFields={allFields}>
      {/* persist={false}: popup-scoped provider under the guest LIST's pref
          keys. Without it the DEFAULT seed's cleanup effect deletes
          `view-guest-<eventId>` on every open — i.e. opening a guest silently
          reset which saved view the list comes back with. Same reasoning (and
          same defect) as the FiltersProvider flag right below. */}
      <ViewsProvider
        type="guest"
        eventId={eventId}
        activeView={view}
        persist={false}
      >
        {/* persist={false}: popup-scoped provider under the guest list's
            pref keys — its DEFAULT seed would otherwise ERASE the list's
            saved filter pref on every open. */}
        <FiltersProvider
          type="guest"
          eventId={eventId}
          activeFilter={filter}
          persist={false}
        >
          <MetaTableProvider
            type="guest"
            eventId={eventId}
            fetch={false}
            initialSortBy="createdAt"
            initialSortOrder="desc"
            initialPageSize={50}
            archivedProp={!!guest.archived}
            totalCountProps={1}
          >
            <GuestsProvider eventId={eventId} initialGuests={[guest]}>
              <GuestLogsProvider eventId={eventId}>
                <GuestCompanionsProvider eventId={eventId}>
                  <GuestAppointmentsProvider eventId={eventId}>
                    <AppointmentGuestProvider
                      eventId={eventId}
                      guests={[]}
                      guestsIds={new Set<string>()}
                    >
                      <RoleProvider>
                        <PGuestDetails
                          guest={guest}
                          avoidStateUpdate
                          onCloseAction={onClose}
                          overlayZClass={zClass}
                        />
                      </RoleProvider>
                    </AppointmentGuestProvider>
                  </GuestAppointmentsProvider>
                </GuestCompanionsProvider>
              </GuestLogsProvider>
            </GuestsProvider>
          </MetaTableProvider>
        </FiltersProvider>
      </ViewsProvider>
    </FieldsProvider>
    </TooltipLayer>
  );
}
